import { SpinnerOverlay } from "components/SpinnerOverlay";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { Navigate } from "react-router-dom";
import { Home } from "routes";

/**
 * This component is used to redirect the user to the correct page based on their permissions.
 * So that we never just send a user to a blank page.
 *
 * Our preferred order for redirection from root is:
 * 1. Chemicals Overview
 * 2. Waste Overview
 * 3. Insights
 * 4. Facilities
 * 5. Contacts
 * 6. Documents
 * 7. Credentials
 * 8. Generic Home
 */
export function CustomerRootRedirect() {
  const { hasPermissions } = useAuthorization();
  const { tenant, loading: loadingTenant } = useTenant();
  const { loading: loadingUser } = useCurrentUser();
  const { featureFlags, loading: loadingFeatureFlags } = useFeatureFlags();

  // Wait for both tenant and user data to be loaded before making permission checks
  if (loadingTenant || loadingUser || loadingFeatureFlags) {
    return <SpinnerOverlay loading />;
  }

  if (!tenant?.id) {
    return <Navigate to="/" replace />;
  }

  if (featureFlags[FeatureFlagKey.TierIIModule]) {
    return <Navigate to={`/o/${tenant.id}/chemicals/overview`} replace />;
  }

  if (featureFlags[FeatureFlagKey.WasteModule]) {
    return <Navigate to={`/o/${tenant.id}/waste/overview`} replace />;
  }

  if (hasPermissions([Permission.RouteInsights])) {
    return <Navigate to={`/o/${tenant.id}/insights/overview`} replace />;
  }

  // Priority order for redirects based on permissions
  // This follows the same logic as the navigation items in CustomerLayout
  if (hasPermissions([Permission.RouteFacilities])) {
    return <Navigate to={`/o/${tenant.id}/facilities`} replace />;
  }

  if (hasPermissions([Permission.RouteContacts])) {
    return <Navigate to={`/o/${tenant.id}/contacts`} replace />;
  }

  if (hasPermissions([Permission.RouteDocuments])) {
    return <Navigate to={`/o/${tenant.id}/documents`} replace />;
  }

  if (hasPermissions([Permission.RouteEpcraCredentials])) {
    return <Navigate to={`/o/${tenant.id}/credentials`} replace />;
  }

  // If no specific permissions, redirect to a safe fallback
  // This could be a "no access" page or back to home
  return <Home name={"Hero"} />;
}
